import { useState, useEffect, useCallback } from 'react';
import StoreOffersAPI, { StoreOffer } from '../api/StoreOffersAPI';
import { toastService } from '../services/ToastService';
import { useTranslation } from 'react-i18next';

type StoreSettings = {
  headerImageUrl?: string;
  headerText?: string;
  showBookAppointmentButton?: boolean;
};

type UseStoreOffersReturn = {
  storeOffers: StoreOffer[];
  isLoading: boolean;
  isStoreEnabled: boolean;
  isUpdatingEnabled: boolean;
  storeSettings: StoreSettings;
  isUpdatingSettings: boolean;
  getStoreOffers: () => Promise<void>;
  getStoreOfferById: (id: number) => Promise<StoreOffer | null>;
  createStoreOffer: (offer: StoreOffer) => Promise<StoreOffer | null>;
  updateStoreOffer: (id: number, offer: StoreOffer) => Promise<StoreOffer | null>;
  deleteStoreOffer: (id: number) => Promise<boolean>;
  setStoreLink: (alias: string) => Promise<string | null>;
  reorderStoreOffers: (reorderedOffers: StoreOffer[]) => Promise<void>;
  updateStoreEnabledStatus: (enabled: boolean) => Promise<void>;
  updateStoreSettings: (settings: StoreSettings) => Promise<void>;
};
// 
// For now, using placeholder images that will display correctly
const MOCK_STORE_OFFERS: StoreOffer[] = [
  {
    id: 1,
    name: 'Adele',
    description: 'A stunning ballgown with intricate lace details and a flowing train. Perfect for a classic wedding ceremony.',
    price: 2500,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Adele.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Adele.jpg',
    gownType: 'ballgown',
    gownStyle: 'classic',
    gownColor: 'white',
    size: 'M',
    available: true,
    quantity: 3,
    featured: true,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 2,
    name: 'Afrodita',
    description: 'A sleek mermaid silhouette with modern minimalist design. Features a fitted bodice and dramatic flare.',
    price: 3200,
    currency: 'EUR',
    images: [
      'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=800&q=80',
      'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&q=80',
    ],
    featuredImage: 'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=800&q=80',
    gownType: 'mermaid',
    gownStyle: 'modern',
    gownColor: 'ivory',
    size: 'S',
    available: true,
    quantity: 1,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 3,
    name: 'Alice',
    description: 'A romantic A-line dress with delicate floral appliqués and soft tulle layers. Ideal for a garden wedding.',
    price: 1800,
    currency: 'EUR',
    images: [
      'https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=800&q=80',
      'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=800&q=80',
    ],
    featuredImage: 'https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=800&q=80',
    gownType: 'a-line',
    gownStyle: 'romantic',
    gownColor: 'blush',
    size: 'L',
    available: true,
    quantity: 3,
    featured: true,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 4,
    name: 'Angelina',
    description: 'A timeless vintage-inspired sheath dress with art deco beading and elegant simplicity.',
    price: 2200,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Angelina..jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Angelina..jpg',
    gownType: 'sheath',
    gownStyle: 'vintage',
    gownColor: 'champagne',
    size: 'M',
    available: true,
    quantity: 2,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 5,
    name: 'Aria',
    description: 'A bohemian-inspired trumpet gown with flowing sleeves and intricate embroidery. Perfect for an outdoor celebration.',
    price: 1950,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Aria.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Aria.jpg',
    gownType: 'trumpet',
    gownStyle: 'boho',
    gownColor: 'ivory',
    size: 'S',
    available: true,
    quantity: undefined,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 6,
    name: 'Ashley',
    description: 'Clean lines and sophisticated simplicity. A minimalist A-line dress for the modern bride.',
    price: 1600,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Ashley.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Ashley.jpg',
    gownType: 'a-line',
    gownStyle: 'minimalist',
    gownColor: 'white',
    size: 'M',
    available: false,
    quantity: 0,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 8,
    name: 'Camelia',
    description: 'A romantic mermaid silhouette with delicate lace overlay and sweetheart neckline.',
    price: 2400,
    currency: 'EUR',
    images: [
      'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=800&q=80',
      'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&q=80',
    ],
    featuredImage: 'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=800&q=80',
    gownType: 'mermaid',
    gownStyle: 'romantic',
    gownColor: 'blush',
    size: 'M',
    available: true,
    quantity: 2,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 9,
    name: 'Flori',
    description: 'A beautiful floral-inspired gown with delicate details and elegant design.',
    price: 2100,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Flori.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Flori.jpg',
    gownType: 'a-line',
    gownStyle: 'romantic',
    gownColor: 'white',
    size: 'M',
    available: true,
    quantity: 2,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 10,
    name: 'Jardine',
    description: 'An elegant garden-inspired dress perfect for outdoor celebrations.',
    price: 1900,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Jardine.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Jardine.jpg',
    gownType: 'a-line',
    gownStyle: 'boho',
    gownColor: 'ivory',
    size: 'S',
    available: true,
    quantity: 1,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 11,
    name: 'Liz',
    description: 'A classic and timeless design with modern elegance.',
    price: 2300,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Liz.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Liz.jpg',
    gownType: 'sheath',
    gownStyle: 'classic',
    gownColor: 'white',
    size: 'M',
    available: true,
    quantity: 3,
    featured: true,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 12,
    name: 'Olympia',
    description: 'A majestic gown with regal elegance and sophisticated design.',
    price: 3500,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Olympia.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Olympia.jpg',
    gownType: 'ballgown',
    gownStyle: 'classic',
    gownColor: 'white',
    size: 'L',
    available: true,
    quantity: 1,
    featured: true,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 13,
    name: 'Rose',
    description: 'A romantic and delicate gown inspired by the beauty of roses.',
    price: 2000,
    currency: 'EUR',
    images: [
      'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Rose.jpg',
    ],
    featuredImage: 'https://agnieszkaswiatly.com/wp-content/uploads/2022/09/Rose.jpg',
    gownType: 'a-line',
    gownStyle: 'romantic',
    gownColor: 'blush',
    size: 'M',
    available: true,
    quantity: 2,
    featured: false,
    onSale: false,
    salePrice: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export function useStoreOffers(): UseStoreOffersReturn {
  const { t } = useTranslation();
  const [storeOffers, setStoreOffers] = useState<StoreOffer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStoreEnabled, setIsStoreEnabled] = useState<boolean>(true);
  const [isUpdatingEnabled, setIsUpdatingEnabled] = useState<boolean>(false);
  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    headerImageUrl: '',
    headerText: '',
    showBookAppointmentButton: true,
  });
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);

  const getStoreOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use mock data for now - replace with API call when backend is ready
      // const response = await StoreOffersAPI.getAll();
      // setStoreOffers(response.data);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setStoreOffers(MOCK_STORE_OFFERS);
    } catch (err) {
      toastService.showToast(t('toast.errors.store_offers.load'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const getStoreOfferById = useCallback(
    async (id: number): Promise<StoreOffer | null> => {
      setIsLoading(true);
      try {
        const response = await StoreOffersAPI.getById(id);
        return response.data;
      } catch (err) {
        toastService.showToast(t('toast.errors.store_offers.load'), 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const createStoreOffer = useCallback(
    async (offer: StoreOffer): Promise<StoreOffer | null> => {
      setIsLoading(true);
      try {
        // Use mock data for now - replace with API call when backend is ready
        // const response = await StoreOffersAPI.create(offer);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        let newOffer: StoreOffer | null = null;
        setStoreOffers((prev) => {
          const maxId = prev.length > 0 ? Math.max(...prev.map(o => o.id || 0)) : 0;
          newOffer = {
            ...offer,
            id: maxId + 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          toastService.showToast(t('toast.success.store_offers.create') || 'Offer created successfully', 'success');
          return [...prev, newOffer];
        });
        return newOffer;
      } catch (err) {
        toastService.showToast(t('toast.errors.store_offers.create') || 'Error creating offer', 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const updateStoreOffer = useCallback(
    async (id: number, offer: StoreOffer): Promise<StoreOffer | null> => {
      setIsLoading(true);
      try {
        // Use mock data for now - replace with API call when backend is ready
        // const response = await StoreOffersAPI.update(id, offer);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        const updatedOffer: StoreOffer = {
          ...offer,
          id,
          updatedAt: new Date().toISOString(),
        };
        setStoreOffers((prev) =>
          prev.map((so) => (so.id === id ? updatedOffer : so))
        );
        toastService.showToast(t('toast.success.store_offers.update') || 'Offer updated successfully', 'success');
        return updatedOffer;
      } catch (err) {
        toastService.showToast(t('toast.errors.store_offers.update') || 'Error updating offer', 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const deleteStoreOffer = useCallback(
    async (id: number): Promise<boolean> => {
      setIsLoading(true);
      try {
        // Use mock data for now - replace with API call when backend is ready
        // await StoreOffersAPI.delete(id);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        setStoreOffers((prev) => prev.filter((so) => so.id !== id));
        toastService.showToast(t('toast.success.store_offers.delete') || 'Offer deleted successfully', 'success');
        return true;
      } catch (err) {
        toastService.showToast(t('toast.errors.store_offers.delete') || 'Error deleting offer', 'error');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const setStoreLink = useCallback(
    async (alias: string): Promise<string | null> => {
      setIsLoading(true);
      try {
        const response = await StoreOffersAPI.setStoreLink(alias);
        return response.data;
      } catch (err) {
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const reorderStoreOffers = useCallback(
    async (reorderedOffers: StoreOffer[]): Promise<void> => {
      setIsLoading(true);
      try {
        // Use mock data for now - replace with API call when backend is ready
        // await StoreOffersAPI.reorder(reorderedOffers.map((o, i) => ({ id: o.id, order: i })));
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        setStoreOffers(reorderedOffers);
        toastService.showToast(t('toast.success.store_offers.reorder') || 'Order updated successfully', 'success');
      } catch (err) {
        toastService.showToast(t('toast.errors.store_offers.reorder') || 'Error updating order', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const updateStoreEnabledStatus = useCallback(
    async (enabled: boolean): Promise<void> => {
      setIsUpdatingEnabled(true);
      try {
        // Use mock for now - replace with API call when backend is ready
        // await StoreOffersAPI.updateEnabledStatus(enabled);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        setIsStoreEnabled(enabled);
        toastService.showToast(
          enabled 
            ? (t('toast.success.store.enabled') || 'Store enabled successfully')
            : (t('toast.success.store.disabled') || 'Store disabled successfully'),
          'success'
        );
      } catch (err) {
        setIsStoreEnabled(!enabled); // Revert on error
        toastService.showToast(t('toast.errors.store.update_enabled') || 'Error updating store status', 'error');
      } finally {
        setIsUpdatingEnabled(false);
      }
    },
    [t]
  );

  const updateStoreSettings = useCallback(
    async (settings: StoreSettings): Promise<void> => {
      setIsUpdatingSettings(true);
      try {
        // Use mock for now - replace with API call when backend is ready
        // await StoreOffersAPI.updateStoreSettings(settings);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        setStoreSettings(settings);
        toastService.showToast(t('toast.success.store.settings_updated') || 'Store settings updated successfully', 'success');
      } catch (err) {
        toastService.showToast(t('toast.errors.store.update_settings') || 'Error updating store settings', 'error');
      } finally {
        setIsUpdatingSettings(false);
      }
    },
    [t]
  );

  useEffect(() => {
    getStoreOffers();
    // Load enabled status
    const loadEnabledStatus = async () => {
      try {
        // Use mock for now - replace with API call when backend is ready
        // const response = await StoreOffersAPI.getEnabledStatus();
        // setIsStoreEnabled(response.data.enabled);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 200));
        setIsStoreEnabled(true); // Default to enabled
      } catch (err) {
        console.error('Error loading store enabled status:', err);
        setIsStoreEnabled(true); // Default to enabled on error
      }
    };
    // Load store settings
    const loadStoreSettings = async () => {
      try {
        // Use mock for now - replace with API call when backend is ready
        // const response = await StoreOffersAPI.getStoreSettings();
        // setStoreSettings(response.data);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 200));
        setStoreSettings({
          headerImageUrl: 'https://www.lbrbridal.com/wp-content/uploads/2020/07/header.png',
          headerText: 'Discover our exquisite collection of wedding dresses',
          showBookAppointmentButton: true,
        });
      } catch (err) {
        console.error('Error loading store settings:', err);
        setStoreSettings({
          headerImageUrl: '',
          headerText: '',
          showBookAppointmentButton: true,
        });
      }
    };
    loadEnabledStatus();
    loadStoreSettings();
  }, [getStoreOffers]);

  return {
    storeOffers,
    isLoading,
    isStoreEnabled,
    isUpdatingEnabled,
    storeSettings,
    isUpdatingSettings,
    getStoreOffers,
    getStoreOfferById,
    createStoreOffer,
    updateStoreOffer,
    deleteStoreOffer,
    setStoreLink,
    reorderStoreOffers,
    updateStoreEnabledStatus,
    updateStoreSettings
  };
}

